import type { BoardObject, Viewport } from '../interfaces/board-object.interface.js';

/**
 * Event types fired by BoardState. Consumers (the renderer, controllers)
 * subscribe via `state.on(type, listener)` and mutate the Konva scene in
 * response.
 */
export type BoardStateEventMap = {
  'object-added':     { object: BoardObject };
  'object-removed':   { id: string };
  'object-updated':   { object: BoardObject };
  'object-reordered': { id: string };
  'viewport-changed': { viewport: Viewport };
  'state-replaced':   { objects: ReadonlyArray<BoardObject> };
};

type Listener<TPayload> = (payload: TPayload) => void;

/**
 * The single source of truth for the board. Holds the object map and the
 * viewport; emits fine-grained events whenever either changes.
 *
 * Deliberately not a Lit ReactiveController — the state is consumed by
 * multiple controllers AND the imperative Konva renderer, and Lit-triggered
 * full re-renders would tear down the Konva stage. Commands mutate state
 * through this class; the renderer reacts to events and updates Konva nodes
 * surgically.
 */
export class BoardState {
  private readonly objects = new Map<string, BoardObject>();
  private _viewport: Viewport = { x: 0, y: 0, scale: 1 };

  private readonly listeners: { [K in keyof BoardStateEventMap]: Set<Listener<BoardStateEventMap[K]>> } = {
    'object-added':     new Set(),
    'object-removed':   new Set(),
    'object-updated':   new Set(),
    'object-reordered': new Set(),
    'viewport-changed': new Set(),
    'state-replaced':   new Set(),
  };

  // ---- objects ---------------------------------------------------------

  addObject(object: BoardObject): void {
    this.objects.set(object.id, object);
    this.emit('object-added', { object });
  }

  /**
   * Atomically replace the entire object map with the given input. Preserves
   * the input array's order as the new insertion order. Emits a single
   * `state-replaced` event with the new objects snapshot. The internal
   * listener Sets are NOT recreated, so previously registered listeners
   * remain attached (FC-6: subject stability, no recreate).
   */
  replaceAll(objects: ReadonlyArray<BoardObject>): void {
    this.objects.clear();
    for (const obj of objects) this.objects.set(obj.id, obj);
    this.emit('state-replaced', { objects: Array.from(this.objects.values()) });
  }

  removeObject(id: string): void {
    if (!this.objects.has(id)) return;
    this.objects.delete(id);
    this.emit('object-removed', { id });
  }

  /**
   * @deprecated Use {@link replaceObject} instead. `updateObject` will be
   * removed once all controllers migrate to the immutable replace pattern
   * driven by `withPatch` from `@o2d/shared`.
   */
  updateObject(object: BoardObject): void {
    if (!this.objects.has(object.id)) return;
    this.objects.set(object.id, object);
    this.emit('object-updated', { object });
  }

  /**
   * Replace an object in the map with a new reference. Idempotent for unknown
   * ids (no-op, no event). Warns in DEV when `next.id !== id` to surface
   * accidental id mismatches; the call still proceeds against `id` for safety.
   */
  replaceObject(id: string, next: BoardObject): void {
    if (!this.objects.has(id)) return;
    if (next.id !== id) {
      console.warn(
        `[BoardState.replaceObject] id mismatch: target="${id}" but next.id="${next.id}". ` +
        `Storing under "${id}".`,
      );
    }
    this.objects.set(id, next);
    this.emit('object-updated', { object: next });
  }

  getObject(id: string): BoardObject | undefined {
    return this.objects.get(id);
  }

  allObjects(): ReadonlyArray<BoardObject> {
    return Array.from(this.objects.values());
  }

  /** Returns the current insertion-order index of an object (0 = bottom). */
  getObjectIndex(id: string): number {
    let i = 0;
    for (const key of this.objects.keys()) {
      if (key === id) return i;
      i++;
    }
    return -1;
  }

  /**
   * Move an object to the top of the z-order (rendered last = on top).
   * Achieved by deleting and re-inserting the entry — Map preserves
   * insertion order, so the re-inserted key ends up at the end.
   */
  moveToTop(id: string): void {
    const obj = this.objects.get(id);
    if (!obj) return;
    this.objects.delete(id);
    this.objects.set(id, obj);
    this.emit('object-reordered', { id });
  }

  /** Move an object to the bottom of the z-order (rendered first = behind everything). */
  moveToBottom(id: string): void {
    const obj = this.objects.get(id);
    if (!obj) return;
    const entries = Array.from(this.objects.entries()).filter(([k]) => k !== id);
    this.objects.clear();
    this.objects.set(id, obj);
    for (const [k, v] of entries) this.objects.set(k, v);
    this.emit('object-reordered', { id });
  }

  /**
   * Restore an object to a specific index in the insertion order.
   * Used by ZOrderCommand.revert() so undo/redo work correctly.
   */
  moveToIndex(id: string, index: number): void {
    const obj = this.objects.get(id);
    if (!obj) return;
    const entries = Array.from(this.objects.entries()).filter(([k]) => k !== id);
    entries.splice(Math.max(0, Math.min(index, entries.length)), 0, [id, obj]);
    this.objects.clear();
    for (const [k, v] of entries) this.objects.set(k, v);
    this.emit('object-reordered', { id });
  }

  // ---- viewport --------------------------------------------------------

  get viewport(): Viewport {
    return { ...this._viewport };
  }

  setViewport(viewport: Viewport): void {
    this._viewport = { ...viewport };
    this.emit('viewport-changed', { viewport: this.viewport });
  }

  // ---- events ----------------------------------------------------------

  on<K extends keyof BoardStateEventMap>(type: K, listener: Listener<BoardStateEventMap[K]>): () => void {
    this.listeners[type].add(listener);
    return () => this.listeners[type].delete(listener);
  }

  private emit<K extends keyof BoardStateEventMap>(type: K, payload: BoardStateEventMap[K]): void {
    for (const listener of this.listeners[type]) listener(payload);
  }
}
